import {
  type Answers,
  conditional,
  defineFlow,
  type FlowState,
  type FlowValues,
  nextSync,
  result,
  runSync,
  type StepName,
  setInputs,
  start,
} from "@fieldtripjs/core";
import { z } from "zod";
import type { AddressSuggestion } from "@/features/geo/shared/address-types";
import { isValidPostalCode } from "@/features/geo/shared/postal-codes";
import { CALL_TIMES, TIME_SLOTS } from "@/features/leads/shared/list-answers";
import { PRIORITY_OPTIONS } from "@/features/leads/shared/priority";
import { START_TIMING_OPTIONS } from "@/features/leads/shared/start-timing";
import {
  AUDIENCES,
  type Audience,
  BUSINESS_FREQUENCIES,
  BUSINESS_ONE_TIME_TYPES,
  BUSINESS_PREFERENCES,
  BUSINESS_TYPES,
  type InitialFields,
  ONE_TIME_TYPES,
  optionListsFor,
  PRIVAT_FREQUENCIES,
  PRIVAT_PREFERENCES,
  PROPERTY_TYPES,
} from "@/features/quote/shared/quote-config";
import { isValidSquareMeters } from "@/features/quote/shared/validation";
import type { UtmParams } from "@/shared/analytics/useUtmParams";
import { toE164 } from "@/shared/lib/phone";

// Without an audience both lists apply, and they share values ("ugentligt").
const oneOf = <T extends string>(options: readonly { value: T }[]) =>
  z.enum([...new Set(options.map((o) => o.value))]);

const typed = z.string().trim().min(1);

const offered = <T extends string>(options: readonly { value: T }[], value: string): value is T =>
  options.some((o) => o.value === value);

const NOT_OFFERED = "Vælg en af mulighederne";

export function makeQuoteFlow({
  addressLookup,
  lockedAudience,
}: {
  addressLookup: boolean;
  lockedAudience: Audience | null;
}) {
  // Until the audience is known the privat steps stand in, so the progress bar has a length.
  const audienceOf = (all: { audience?: { audience: Audience } }) =>
    lockedAudience ?? all.audience?.audience ?? null;
  const isErhverv = (all: { audience?: { audience: Audience } }) => audienceOf(all) === "erhverv";
  const optionsFor = (all: { audience?: { audience: Audience } }) =>
    optionListsFor(audienceOf(all));

  return defineFlow({
    start: "audience",
    steps: {
      audience: {
        fields: { audience: oneOf(AUDIENCES) },
        visibleWhen: () => lockedAudience === null,
        next: "business",
      },
      business: {
        fields: { business: oneOf(BUSINESS_TYPES) },
        visibleWhen: isErhverv,
        next: "task",
      },
      task: { fields: { task: z.enum(["lobende", "engangs"]) }, next: "subtype" },
      subtype: {
        fields: {
          engangsSubtype: oneOf([...ONE_TIME_TYPES, ...BUSINESS_ONE_TIME_TYPES]),
          engangsAndetDescription: conditional(typed),
        },
        fieldsVisibleWhen: { engangsAndetDescription: (v) => v.engangsSubtype === "andet" },
        visibleWhen: (all) => all.task?.task === "engangs",
        validate: (v, all) =>
          offered(optionsFor(all).subtypes, v.engangsSubtype)
            ? undefined
            : { engangsSubtype: [NOT_OFFERED] },
        next: "address",
      },
      address: {
        fields: {
          addressSkipped: z.boolean().default(false),
          address: conditional(z.custom<AddressSuggestion>((value) => value != null)),
          streetAddress: z.string().default(""),
        },
        fieldsVisibleWhen: { address: (v) => !v.addressSkipped },
        visibleWhen: () => addressLookup,
        next: "property",
      },
      property: {
        fields: { propertyType: oneOf(PROPERTY_TYPES), floor: conditional(z.string().min(1)) },
        fieldsVisibleWhen: { floor: (v) => v.propertyType === "lejlighed" },
        visibleWhen: (all) => !isErhverv(all),
        next: "sqm",
      },
      sqm: {
        fields: { squareMeters: z.string().refine(isValidSquareMeters).transform(Number) },
        next: "frequency",
      },
      frequency: {
        fields: { frequency: oneOf([...PRIVAT_FREQUENCIES, ...BUSINESS_FREQUENCIES]) },
        visibleWhen: (all) => all.task?.task === "lobende",
        validate: (v, all) =>
          offered(optionsFor(all).frequencies, v.frequency)
            ? undefined
            : { frequency: [NOT_OFFERED] },
        next: "start",
      },
      start: {
        fields: {
          startTiming: oneOf(START_TIMING_OPTIONS),
          startDate: conditional(z.string().min(1)),
        },
        fieldsVisibleWhen: { startDate: (v) => v.startTiming === "SPECIFIK_DATO" },
        next: "timing",
      },
      timing: {
        fields: { timeSlots: z.array(oneOf(TIME_SLOTS.options)).min(1) },
        visibleWhen: isErhverv,
        next: "preferences",
      },
      preferences: {
        fields: {
          preferences: z.array(oneOf([...PRIVAT_PREFERENCES, ...BUSINESS_PREFERENCES])).default([]),
        },
        validate: (v, all) =>
          v.preferences.every((p) => offered(optionsFor(all).preferences, p))
            ? undefined
            : { preferences: [NOT_OFFERED] },
        next: "priority",
      },
      priority: { fields: { priority: oneOf(PRIORITY_OPTIONS) }, next: "postal" },
      postal: { fields: { postalCode: z.string().refine(isValidPostalCode) }, next: "callTime" },
      callTime: {
        fields: { callTimes: z.array(oneOf(CALL_TIMES.options)).min(1) },
        visibleWhen: (all) => !isErhverv(all),
        next: "contact",
      },
      contact: {
        fields: {
          contactName: typed,
          companyName: conditional(typed),
          email: typed,
          phone: typed,
          consent: z.boolean().refine((given) => given),
          notes: z.string().trim().default(""),
        },
        fieldsVisibleWhen: { companyName: (_, all) => isErhverv(all) },
      },
    },
  });
}

export type QuoteFlow = ReturnType<typeof makeQuoteFlow>;
export type StepId = StepName<QuoteFlow>;
export type QuoteState = FlowState<QuoteFlow>;

export const STEP_PHASES: Record<StepId, string> = {
  audience: "Kom i gang",
  business: "Opgaven",
  task: "Opgaven",
  subtype: "Opgaven",
  address: "Adresse",
  property: "Detaljer",
  sqm: "Detaljer",
  frequency: "Detaljer",
  start: "Detaljer",
  timing: "Detaljer",
  preferences: "Detaljer",
  priority: "Detaljer",
  postal: "Detaljer",
  callTime: "Kontakt",
  contact: "Kontakt",
};

const isBlank = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

// Optional preferences count as answered only when named, or the customer would never see them.
export function linkAnswers(f: Partial<InitialFields>): Answers<QuoteFlow> {
  const steps: Partial<Record<StepId, Record<string, unknown>>> = {
    audience: { audience: f.audience },
    business: { business: f.business },
    task: { task: f.task },
    subtype: { engangsSubtype: f.engangsSubtype },
    property: { propertyType: f.propertyType, floor: f.floor },
    sqm: { squareMeters: f.squareMeters },
    frequency: { frequency: f.frequency },
    start: { startTiming: f.startTiming, startDate: f.startDate },
    timing: { timeSlots: f.timeSlots },
    preferences: { preferences: f.preferences },
    postal: { postalCode: f.postalCode },
    contact: { notes: f.notes },
  };
  return Object.fromEntries(
    Object.entries(steps).filter(([, inputs]) => !Object.values(inputs).every(isBlank)),
  );
}

// `runSync` keeps only the answers of the steps it played; the rest wait for the customer.
export function startState(
  flow: QuoteFlow,
  answers: Answers<QuoteFlow>,
  { fromStart = false } = {},
): QuoteState {
  return Object.entries(answers).reduce(
    (state, [step, inputs]) => setInputs(flow, state, inputs ?? {}, step as StepId),
    fromStart ? start(flow) : runSync(flow, answers).state,
  );
}

// The customer's latest choice, also while they stand on the audience step.
export function audienceIn(state: QuoteState, lockedAudience: Audience | null): Audience | null {
  const chosen = state.inputs.audience?.audience;
  return lockedAudience ?? (chosen === "privat" || chosen === "erhverv" ? chosen : null);
}

// An answer the new audience doesn't offer would sit invisible and keep Next disabled.
export function audienceSwitch(state: QuoteState, audience: Audience): Answers<QuoteFlow> {
  const lists = optionListsFor(audience);
  const { subtype, frequency, timing, preferences, callTime } = state.inputs;
  const one = <T extends string>(options: readonly { value: T }[], value: unknown) =>
    typeof value === "string" && offered(options, value) ? value : undefined;
  const some = <T extends string>(options: readonly { value: T }[], values: unknown) =>
    Array.isArray(values) ? values.filter((v): v is T => offered(options, v)) : undefined;
  return {
    audience: { audience },
    subtype: { engangsSubtype: one(lists.subtypes, subtype?.engangsSubtype) },
    frequency: { frequency: one(lists.frequencies, frequency?.frequency) },
    timing: { timeSlots: some(lists.timeSlots, timing?.timeSlots) },
    preferences: { preferences: some(lists.preferences, preferences?.preferences) },
    callTime: { callTimes: some(lists.callTimes, callTime?.callTimes) },
  };
}

type PayloadContext = {
  utmParams: UtmParams;
  eventId: string;
  honeypot: string;
  isRepeatTask: boolean;
};

type UnionToIntersection<U> = (U extends unknown ? (u: U) => void : never) extends (
  i: infer I,
) => void
  ? I
  : never;
type Answered = Partial<UnionToIntersection<NonNullable<FlowValues<QuoteFlow>[StepId]>>>;

// Submitting is the contact step's Next, so that step is validated here.
export function buildLeadPayload(flow: QuoteFlow, state: QuoteState, context: PayloadContext) {
  const done = nextSync(flow, state);
  if (done.state.status !== "complete")
    throw new Error("buildLeadPayload needs every step answered");
  const values = result(flow, done.state);
  const answered: Answered = Object.assign({}, ...Object.values(values));
  const { audience, task, address, addressSkipped, phone, preferences = [], ...answers } = answered;
  const preferenceList = answers.business ? BUSINESS_PREFERENCES : PRIVAT_PREFERENCES;

  return {
    // Honeypot: must be in the payload so the route can see it. Empty = human;
    // filled = bot, and /api/lead silently drops the submission.
    ref_token: context.honeypot,
    ...answers,
    clientType: answers.business ? "erhverv" : "privat",
    taskType: task,
    recurring: task === "lobende",
    adgangsadresseId: address?.adresse.id,
    phone: toE164(phone),
    preferences,
    preferencesLabels: preferences
      .map((v) => preferenceList.find((p) => p.value === v)?.label)
      .filter((l): l is string => Boolean(l))
      .join(", "),
    isRepeatTask: context.isRepeatTask,
    ...context.utmParams,
    eventId: context.eventId,
  };
}
